import type { PropsWithChildren } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom-v5-compat';

import { getListVaultsQueryKey } from '@/modules/api/codegen';
import { Form, handleFormSubmit, useTranslateSubmitError } from '@/modules/forms';

import { Fields } from '../../constants';
import { passwordDefaultValues } from '../../constants/defaultValues';
import { secretFormSchema, type SecretFormSchemaType, type SecretFormValues } from '../../schemas';
import { createSecret } from '../../services/actions';

export interface NewSecretFormProviderProps extends PropsWithChildren {}

export const NewSecretFormProvider = ({ children }: NewSecretFormProviderProps) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    // Set by AddSecretButton when the user came from a vault, so the record starts there.
    const { state } = useLocation();

    const translateSubmitError = useTranslateSubmitError<SecretFormValues>();

    return (
        <Form<SecretFormSchemaType, SecretFormValues>
            defaultValues={{ ...passwordDefaultValues, [Fields.VAULT_ID]: state?.vaultId ?? null }}
            schema={secretFormSchema}
            onSubmit={async (values, { setError }) => {
                const error = await handleFormSubmit<SecretFormValues>((values, actions) => {
                    dispatch(createSecret(values, actions));
                })(values);

                if (error) {
                    const translatedError = translateSubmitError(...error);
                    setError(...translatedError);
                    return;
                }

                // The saga owns the request, so the vault counts are refreshed here, where the result lands.
                queryClient.invalidateQueries({ queryKey: getListVaultsQueryKey() });
            }}
        >
            {children}
        </Form>
    );
};
